import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContentBox from '../components/ContentBox';
import { getArticlesByCategory, getPublicCategories, searchArticles } from '../services/api';

const EARLIEST_PUBLIC_DATE = '2026-02-01';

const SPOTLIGHT_COLORS = {
    1: { backgroundColor: '#c48a00', color: '#2b1d00' },
    2: { backgroundColor: '#2d6a1e', color: '#f5f1e8' },
    3: { backgroundColor: '#4f9ea4', color: '#10282b' },
    4: { backgroundColor: '#6d1b1b', color: '#f5f1e8' },
    5: { backgroundColor: '#c86aa0', color: '#2f1021' },
    6: { backgroundColor: '#6c2d82', color: '#f5f1e8' }
};

const DEFAULT_SPOTLIGHT = SPOTLIGHT_COLORS[4];

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const slugifyCategory = (value = '') => value.toLowerCase().replace(/\s+/g, '');

const getSpotlightStyle = (spotlightColorId) => {
    const theme = SPOTLIGHT_COLORS[Number(spotlightColorId)] || DEFAULT_SPOTLIGHT;

    return {
        backgroundColor: theme.backgroundColor,
        color: theme.color,
        boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)',
    };
};

function ArticleCard({ article }) {
    const isGuide = article.categoryName?.toLowerCase().includes('guide');
    const placeholderImg = isGuide ? 'frankly_guides.png' : 'frankly_news.png';

    return (
        <Link
            to={article.href}
            className="group flex flex-col bg-offwhite border border-gray-300 shadow-[0_3px_0_0_rgba(0,0,0,0.2)] hover:shadow-[0_3px_0_0_rgba(0,0,0,0.7)] transition-shadow overflow-hidden"
        >
            <div className="relative overflow-hidden bg-gray-200 h-[160px]">
                {article.headerImage ? (
                    <img
                        src={article.headerImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        style={{ imageRendering: 'auto' }}
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextSibling.style.display = 'flex';
                        }}
                    />
                ) : null}

                <div
                    className="w-full h-full items-center justify-center bg-gray-200"
                    style={{ display: article.headerImage ? 'none' : 'flex' }}
                >
                    <img
                        src={`${process.env.PUBLIC_URL}/img/${placeholderImg}`}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                </div>

                <div className="absolute top-1 left-2">
                    <span
                        className="font-['Jersey_10'] uppercase text-xs tracking-widest px-2 py-0.5"
                        style={getSpotlightStyle(article.spotlightColorId)}
                    >
                        {article.categoryName}
                    </span>
                </div>
            </div>

            <div className="flex flex-col flex-1 p-3 gap-2">
                <h2
                    className="font-['Jersey_10'] uppercase text-lg tracking-widest text-gray-900 leading-tight group-hover:text-maroon transition-colors"
                    style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {article.title}
                </h2>

                {article.flavour && (
                    <p
                        className="text-gray-600 text-sm leading-relaxed flex-1"
                        style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {article.flavour}
                    </p>
                )}

                <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-200">
                    <span className="text-sm text-gray-500 font-['Jersey_10'] uppercase tracking-widest">
                        {article.author}
                    </span>
                    <span className="text-sm text-gray-400 font-['Jersey_10'] uppercase tracking-widest">
                        {formatDate(article.publicDate)}
                    </span>
                </div>
            </div>
        </Link>
    );
}

const PaginationButton = ({ children, onClick, disabled, active }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        className={`px-4 py-1 text-md font-['Jersey_10'] tracking-widest uppercase transition-all border ${
            active
                ? 'bg-gold/60 border-gold text-coal_dark'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50'
        }`}
    >
        {children}
    </button>
);

function ArticleCategories() {
    const { categorySlug = 'all' } = useParams();
    const navigate = useNavigate();
    const dropdownRef = useRef(null);

    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({});
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);

    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateDraftStart, setDateDraftStart] = useState('');
    const [dateDraftEnd, setDateDraftEnd] = useState('');

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const interactionCooldownRef = useRef(0);
    const INTERACTION_COOLDOWN = 1000;

    const hasActiveSearch = useMemo(() => {
        return Boolean(searchTerm || startDate || endDate);
    }, [searchTerm, startDate, endDate]);

    useEffect(() => {
        getPublicCategories().then((data) => {
            if (data.success) {
                setCategories(data.categories);
            }
        });
    }, []);

    useEffect(() => {
        setPage(1);
    }, [categorySlug, searchTerm, startDate, endDate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setCategoryDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currentCategoryLabel = useMemo(() => {
        if (hasActiveSearch) {
            return 'Search Results';
        }

        if (categorySlug === 'all') return 'Latest Articles';
        const matched = categories.find((cat) => slugifyCategory(cat.name) === categorySlug);
        return matched ? matched.name : 'Articles';
    }, [categories, categorySlug, hasActiveSearch]);

    const selectedCategoryText = useMemo(() => {
        if (categorySlug === 'all') return 'All Articles';
        const matched = categories.find((cat) => slugifyCategory(cat.name) === categorySlug);
        return matched ? matched.name : 'All Articles';
    }, [categories, categorySlug]);

    const canTriggerRequest = () => {
        const now = Date.now();

        if (now - interactionCooldownRef.current < INTERACTION_COOLDOWN) {
            toast.warning('Please wait a moment before filtering again.');
            return false;
        }

        interactionCooldownRef.current = now;
        return true;
    };

    const loadArticles = useCallback(async () => {
        setLoading(true);

        try {
            let data;

            if (hasActiveSearch) {
                data = await searchArticles({
                    q: searchTerm,
                    startDate,
                    endDate,
                    categorySlug: categorySlug !== 'all' ? categorySlug : '',
                    page
                });
            } else {
                data = await getArticlesByCategory(categorySlug, page);
            }

            if (data.success) {
                setArticles(data.articles || []);
                setPagination(data.pagination || {});
            } else {
                setArticles([]);
                setPagination({});
            }
        } catch (err) {
            toast.error('Failed to load articles');
            setArticles([]);
            setPagination({});
        }

        setLoading(false);
    }, [categorySlug, page, hasActiveSearch, searchTerm, startDate, endDate]);

    useEffect(() => {
        loadArticles();
    }, [loadArticles]);

    const handleCategoryChange = (value) => {
        if (!canTriggerRequest()) return;

        setCategoryDropdownOpen(false);
        setPage(1);
        navigate(`/articles/${value}`);
        window.scrollTo(0, 0);
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();

        if (!canTriggerRequest()) return;

        const trimmedSearch = searchInput.trim();

        if (!trimmedSearch && !startDate && !endDate) {
            setSearchTerm('');
            setPage(1);
            return;
        }

        if (startDate && endDate && startDate > endDate) {
            toast.error('Start date cannot be after end date');
            return;
        }

        setSearchTerm(trimmedSearch);
        setPage(1);
        window.scrollTo(0, 0);
    };

    const clearSearchFilters = () => {
        setSearchInput('');
        setSearchTerm('');
        setStartDate('');
        setEndDate('');
        setPage(1);
    };

    const renderPageButtons = () => {
        const buttons = [];
        const maxButtons = 5;
        let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
        let endPage = Math.min(pagination.totalPages || 1, startPage + maxButtons - 1);

        if (endPage - startPage + 1 < maxButtons) {
            startPage = Math.max(1, endPage - maxButtons + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <PaginationButton
                    key={i}
                    active={i === page}
                    onClick={() => {
                        setPage(i);
                        window.scrollTo(0, 0);
                    }}
                >
                    {i}
                </PaginationButton>
            );
        }

        return buttons;
    };

    const resultsSummary = useMemo(() => {
        if (!hasActiveSearch) {
            return null;
        }

        const parts = [];

        if (searchTerm) {
            parts.push(`matching "${searchTerm}"`);
        }

        if (startDate && endDate) {
            parts.push(`from ${formatDate(startDate)} to ${formatDate(endDate)}`);
        } else if (startDate) {
            parts.push(`from ${formatDate(startDate)}`);
        } else if (endDate) {
            parts.push(`up to ${formatDate(endDate)}`);
        }

        if (categorySlug !== 'all') {
            parts.push(`in ${selectedCategoryText}`);
        }

        return parts.length > 0 ? parts.join(' ') : null;
    }, [hasActiveSearch, searchTerm, startDate, endDate, categorySlug, selectedCategoryText]);

    return (
        <div className="w-full p-5">
            <div className="grid grid-cols-1 gap-4 lg2:gap-6 lg2:grid-cols-[260px_minmax(0,1fr)] items-start">
                <div className="min-w-0">
                    <button
                        type="button"
                        onClick={() => setFiltersOpen((prev) => !prev)}
                        className="lg2:hidden w-full mb-4 bg-maroon text-offwhite border border-black shadow-[2px_2px_0px_rgba(0,0,0,0.2)] px-4 py-2 font-['Jersey_10'] uppercase tracking-widest text-[20px] flex items-center justify-between"
                    >
                        <span>Search & Filter</span>
                        <svg
                            className={`w-4 h-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    <div className={`${filtersOpen ? 'block' : 'hidden'} lg2:block`}>
                        <ContentBox header="Search & Filter" theme="default" tint="olive" icon="/img/icons/icon_65.png">
                            <form onSubmit={handleSearchSubmit} className="flex flex-col gap-4">
                                <div>
                                    <label className="block mb-2 font-['Jersey_10'] uppercase tracking-widest text-md text-black/80">
                                        Category
                                    </label>

                                    <div ref={dropdownRef} className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setCategoryDropdownOpen((prev) => !prev)}
                                            className="w-full bg-coal border border-black/70 shadow-[0_3px_0_0_rgba(0,0,0,0.2)] px-3 pr-10 py-2 font-['Jersey_10'] uppercase tracking-widest text-lg text-offwhite text-left relative"
                                        >
                                            {selectedCategoryText}
                                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-offwhite">
                                                <svg
                                                    className={`w-4 h-4 transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </span>
                                        </button>

                                        {categoryDropdownOpen && (
                                            <div className="absolute top-full left-0 right-0 mt-1 z-20 overflow-hidden bg-offwhite border border-black/70 shadow-[0_3px_0_0_rgba(0,0,0,0.2)]">
                                                <button
                                                    type="button"
                                                    onClick={() => handleCategoryChange('all')}
                                                    className={`block w-full text-left px-3 py-2 font-['Jersey_10'] tracking-wider text-xl transition-colors ${
                                                        categorySlug === 'all'
                                                            ? 'bg-gold/50 text-coal_dark'
                                                            : 'bg-offwhite text-coal_dark hover:bg-gold hover:text-coal_dark'
                                                    }`}
                                                >
                                                    All Articles
                                                </button>

                                                {categories.map((cat) => {
                                                    const value = slugifyCategory(cat.name);
                                                    const isActive = categorySlug === value;

                                                    return (
                                                        <button
                                                            type="button"
                                                            key={cat.id}
                                                            onClick={() => handleCategoryChange(value)}
                                                            className={`block w-full text-left px-3 py-2 font-['Jersey_10'] tracking-wider text-xl transition-colors ${
                                                                isActive
                                                                    ? 'bg-gold/50 text-coal_dark'
                                                                    : 'bg-offwhite text-coal_dark hover:bg-gold hover:text-coal_dark'
                                                            }`}
                                                        >
                                                            {cat.name}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="h-[1px] p-0 bg-coal/10 w-full" />

                                <div>
                                    <label className="block mb-2 font-['Jersey_10'] uppercase tracking-widest text-md text-black/80">
                                        Search
                                    </label>

                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            placeholder="Title or phrase..."
                                            className="w-full bg-offwhite border border-black/30 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] px-3 py-2 font-['Jersey_10'] tracking-wider text-lg text-coal_dark placeholder:text-coal/40 focus:outline-none"
                                        />
                                        <button
                                            type="submit"
                                            className="shrink-0 bg-coal text-offwhite border border-black/70 shadow-[0_3px_0_0_rgba(0,0,0,0.2)] px-4 py-2 font-['Jersey_10'] uppercase tracking-widest text-lg hover:bg-coal_dark transition-colors"
                                        >
                                            Search
                                        </button>
                                    </div>
                                </div>

                                <div className="h-[1px] p-0 bg-coal/10 w-full" />

                                <div>
                                    <label className="block mb-2 font-['Jersey_10'] uppercase tracking-widest text-md text-black/80">
                                        Publication Date
                                    </label>

                                    <div className="grid grid-cols-1 gap-2">
                                        <div>
                                            <span className="block mb-1 font-['Jersey_10'] uppercase tracking-widest text-sm text-black/60">
                                                Start Date
                                            </span>
                                            <input
                                                type="date"
                                                min={EARLIEST_PUBLIC_DATE}
                                                max={endDate || undefined}
                                                value={dateDraftStart}
                                                onChange={(e) => setDateDraftStart(e.target.value)}
                                                className="w-full bg-offwhite border border-black/30 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] px-3 py-2 font-['Jersey_10'] tracking-wider text-lg text-coal_dark focus:outline-none"
                                            />
                                        </div>

                                        <div>
                                            <span className="block mb-1 font-['Jersey_10'] uppercase tracking-widest text-sm text-black/60">
                                                End Date
                                            </span>
                                            <input
                                                type="date"
                                                min={startDate || EARLIEST_PUBLIC_DATE}
                                                value={dateDraftEnd}
                                                onChange={(e) => setDateDraftEnd(e.target.value)}
                                                className="w-full bg-offwhite border border-black/30 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] px-3 py-2 font-['Jersey_10'] tracking-wider text-lg text-coal_dark focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-2 pt-1">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!canTriggerRequest()) return;

                                            if (dateDraftStart && dateDraftEnd && dateDraftStart > dateDraftEnd) {
                                                toast.error('Start date cannot be after end date');
                                                return;
                                            }

                                            setStartDate(dateDraftStart);
                                            setEndDate(dateDraftEnd);
                                            setPage(1);
                                        }}
                                        className="flex-1 bg-maroon text-offwhite border border-black/70 shadow-[0_3px_0_0_rgba(0,0,0,0.2)] px-4 py-2 font-['Jersey_10'] uppercase tracking-widest text-lg hover:opacity-95 transition-opacity"
                                    >
                                        Apply
                                    </button>

                                    <button
                                        type="button"
                                        onClick={clearSearchFilters}
                                        className="bg-offwhite text-coal_dark border border-black/30 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] px-4 py-2 font-['Jersey_10'] uppercase tracking-widest text-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Reset
                                    </button>
                                </div>
                            </form>
                        </ContentBox>
                    </div>
                </div>

                <div className="min-w-0">
                    <ContentBox header={currentCategoryLabel} theme="default" tint="maroon">
                        {loading ? (
                            <div className="py-20 text-center font-['Jersey_10'] uppercase text-gray-400 animate-pulse">
                                Loading...
                            </div>
                        ) : (
                            <>
                                {pagination.totalPages > 1 && (
                                    <div className="mb-6 pb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-gray-200">
                                        <span className="text-md text-gray-500 font-['Jersey_10'] uppercase tracking-wide">
                                            Page {page} of {pagination.totalPages} &mdash; {pagination.totalItems} articles
                                        </span>

                                        <div className="flex flex-wrap items-center gap-1">
                                            <PaginationButton
                                                onClick={() => {
                                                    setPage(page - 1);
                                                    window.scrollTo(0, 0);
                                                }}
                                                disabled={!pagination.hasPrevPage}
                                            >
                                                &lsaquo; Prev
                                            </PaginationButton>

                                            {renderPageButtons()}

                                            <PaginationButton
                                                onClick={() => {
                                                    setPage(page + 1);
                                                    window.scrollTo(0, 0);
                                                }}
                                                disabled={!pagination.hasNextPage}
                                            >
                                                Next &rsaquo;
                                            </PaginationButton>
                                        </div>
                                    </div>
                                )}

                                {resultsSummary && (
                                    <div className="mb-4 pb-3 border-b border-gray-200">
                                        <p className="text-sm text-gray-600 font-['Jersey_10'] uppercase tracking-widest">
                                            {resultsSummary}
                                        </p>
                                    </div>
                                )}

                                {articles.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                        {articles.map((article) => (
                                            <ArticleCard key={article.id} article={article} />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="py-16 text-center font-['Jersey_10'] uppercase text-gray-400 tracking-widest text-[20px]">
                                        No articles found
                                    </div>
                                )}

                                {pagination.totalPages > 1 && (
                                    <div className="mt-6 pt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-gray-200">
                                        <span className="text-md text-gray-500 font-['Jersey_10'] uppercase tracking-wide">
                                            Page {page} of {pagination.totalPages} &mdash; {pagination.totalItems} articles
                                        </span>

                                        <div className="flex flex-wrap items-center gap-1">
                                            <PaginationButton
                                                onClick={() => {
                                                    setPage(page - 1);
                                                    window.scrollTo(0, 0);
                                                }}
                                                disabled={!pagination.hasPrevPage}
                                            >
                                                &lsaquo; Prev
                                            </PaginationButton>

                                            {renderPageButtons()}

                                            <PaginationButton
                                                onClick={() => {
                                                    setPage(page + 1);
                                                    window.scrollTo(0, 0);
                                                }}
                                                disabled={!pagination.hasNextPage}
                                            >
                                                Next &rsaquo;
                                            </PaginationButton>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </ContentBox>
                </div>
            </div>
        </div>
    );
}

export default ArticleCategories;